import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Clock, X } from "lucide-react";
import { API } from "../context";

/**
 * Slim, app-wide banner shown only while the signed-in account is on a free
 * trial. Surfaces the days remaining and links to /subscribe so the switch to
 * paid is never a surprise (mirrors the email trial reminder, but in-app).
 */
export default function TrialCountdownBanner({ user }) {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [extending, setExtending] = useState(false);
  const navigate = useNavigate();

  const loadStatus = React.useCallback(() => {
    if (!user?.id) { setStatus(null); return; }
    axios.get(`${API}/payments/trial-status?user_id=${user.id}`)
      .then((r) => setStatus(r.data))
      .catch(() => {});
  }, [user?.id]);

  useEffect(() => {
    let alive = true;
    if (!user?.id) { setStatus(null); return; }
    axios.get(`${API}/payments/trial-status?user_id=${user.id}`)
      .then((r) => { if (alive) setStatus(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [user?.id]);

  const extendTrial = async (e) => {
    e.stopPropagation();
    setExtending(true);
    try {
      await axios.post(`${API}/payments/extend-trial`, { parent_id: user.id });
      loadStatus();
    } catch (_e) {
      // Feature may be disabled, fail quietly.
    } finally {
      setExtending(false);
    }
  };

  if (!user?.id || dismissed || !status?.on_trial) return null;

  const d = status.days_left;
  const urgent = d <= 2;

  return (
    <div
      data-testid="trial-countdown-banner"
      onClick={() => navigate("/subscribe")}
      style={{
        cursor: "pointer",
        position: "fixed",
        left: "50%",
        bottom: 88,
        transform: "translateX(-50%)",
        zIndex: 55,
        maxWidth: "calc(100vw - 24px)",
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 16px",
        borderRadius: 9999,
        fontSize: 13,
        fontWeight: 700,
        color: "#fff",
        boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
        background: urgent
          ? "linear-gradient(90deg,#DC2626,#F97316)"
          : "linear-gradient(90deg,#7C3AED,#4F46E5)",
      }}
    >
      <Clock size={15} className="shrink-0" />
      <span>
        {d} day{d === 1 ? "" : "s"} left in your free trial, tap to keep premium
      </span>
      {status.extension_available && (
        <button
          data-testid="trial-extend-btn"
          onClick={extendTrial}
          disabled={extending}
          style={{
            background: "rgba(255,255,255,0.22)",
            border: "1px solid rgba(255,255,255,0.35)",
            borderRadius: 9999,
            color: "#fff",
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 800,
            padding: "4px 10px",
            flexShrink: 0,
          }}
        >
          {extending ? "…" : "+3 days"}
        </button>
      )}
      <button
        data-testid="trial-banner-dismiss"
        aria-label="Dismiss"
        onClick={(e) => { e.stopPropagation(); setDismissed(true); }}
        style={{
          background: "rgba(255,255,255,0.18)",
          border: "none",
          borderRadius: 9999,
          color: "#fff",
          cursor: "pointer",
          display: "flex",
          padding: 4,
          flexShrink: 0,
        }}
      >
        <X size={14} />
      </button>
    </div>
  );
}
